import {
  BulkDeactivationJobDocument,
  BulkDeactivationJobQuery,
  BulkDeactivationJobQueryVariables,
  StartBulkDeactivationDocument,
  StartBulkDeactivationMutation,
  StartBulkDeactivationMutationVariables,
} from '@/generated/graphql';
import {ApolloClient} from '@apollo/client';

export type BulkJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type DeactivationRun = {
  jobId: string;
  status: BulkJobStatus;
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  startedAt: string | null;
  finishedAt: string | null;
};

export type DeactivationCounts = {
  total: number;
  done: number;
  succeeded: number;
  failed: number;
  isComplete: boolean;
};

type JobShape = {
  id: string;
  status?: string | null;
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  startedAt?: string | null;
  finishedAt?: string | null;
};

const STORAGE_KEY = 'members.bulkDeactivation.v2';
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_FAILURES = 5;
const MAX_POLL_BACKOFF_MS = 30000;

type Listener = () => void;

const listeners = new Set<Listener>();

let run: DeactivationRun | null = null;
let client: ApolloClient<object> | null = null;
let onComplete: (() => void) | null = null;
let onError: ((message: string) => void) | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollFailureCount = 0;
let storageFailureNotified = false;

const notify = () => listeners.forEach(listener => listener());

const persist = () => {
  try {
    if (run) localStorage.setItem(STORAGE_KEY, JSON.stringify(run));
    else localStorage.removeItem(STORAGE_KEY);
    storageFailureNotified = false;
  } catch {
    if (!storageFailureNotified) {
      onError?.('Could not save deactivation progress for reload recovery.');
      storageFailureNotified = true;
    }
  }
};

const isBulkJobStatus = (status: unknown): status is BulkJobStatus =>
  status === 'pending' ||
  status === 'processing' ||
  status === 'completed' ||
  status === 'failed';

const isTerminal = (status: BulkJobStatus) =>
  status === 'completed' || status === 'failed';

const normalizeJobStatus = (status: unknown): BulkJobStatus =>
  isBulkJobStatus(status) ? status : 'pending';

const applyJob = (job: JobShape) => {
  run = {
    jobId: job.id,
    status: normalizeJobStatus(job.status),
    total: job.total,
    processed: job.processed,
    succeeded: job.succeeded,
    failed: job.failed,
    startedAt: job.startedAt ?? null,
    finishedAt: job.finishedAt ?? null,
  };
  persist();
  notify();
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const normalizeDateString = (value: unknown) => {
  if (typeof value !== 'string') return null;
  if (Number.isNaN(Date.parse(value))) return null;
  return value;
};

const normalizeStoredRun = (storedRun: unknown): DeactivationRun | null => {
  if (!storedRun || typeof storedRun !== 'object') return null;

  const candidate = storedRun as Partial<DeactivationRun>;
  if (typeof candidate.jobId !== 'string' || candidate.jobId.length === 0) {
    return null;
  }
  if (!isBulkJobStatus(candidate.status)) return null;
  if (
    !isFiniteNumber(candidate.total) ||
    !isFiniteNumber(candidate.processed) ||
    !isFiniteNumber(candidate.succeeded) ||
    !isFiniteNumber(candidate.failed)
  ) {
    return null;
  }

  return {
    jobId: candidate.jobId,
    status: candidate.status,
    total: candidate.total,
    processed: candidate.processed,
    succeeded: candidate.succeeded,
    failed: candidate.failed,
    startedAt: normalizeDateString(candidate.startedAt),
    finishedAt: normalizeDateString(candidate.finishedAt),
  };
};

const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const schedulePoll = (delayMs = POLL_INTERVAL_MS) => {
  stopPolling();
  pollTimer = setTimeout(poll, delayMs);
};

const failPolling = () => {
  stopPolling();
  run = null;
  persist();
  notify();
  onError?.(
    'Could not check deactivation progress. Please refresh and try again.',
  );
};

const getPollBackoff = () =>
  Math.min(POLL_INTERVAL_MS * 2 ** pollFailureCount, MAX_POLL_BACKOFF_MS);

const shouldRetryPoll = (error: unknown) => {
  const graphQLErrors = (error as {graphQLErrors?: unknown})?.graphQLErrors;
  return !(Array.isArray(graphQLErrors) && graphQLErrors.length > 0);
};

const poll = async () => {
  if (!client || !run) return;
  try {
    const {data} = await client.query<
      BulkDeactivationJobQuery,
      BulkDeactivationJobQueryVariables
    >({
      query: BulkDeactivationJobDocument,
      variables: {id: run.jobId},
      fetchPolicy: 'no-cache',
    });
    const job = data?.bulkDeactivationJob;
    if (!job) {
      stopPolling();
      run = null;
      persist();
      notify();
      return;
    }
    pollFailureCount = 0;
    applyJob(job);
    if (run && isTerminal(run.status)) {
      stopPolling();
      onComplete?.();
      return;
    }
  } catch (error) {
    pollFailureCount += 1;
    if (pollFailureCount > MAX_POLL_FAILURES || !shouldRetryPoll(error)) {
      failPolling();
      pollFailureCount = 0;
      return;
    }
    schedulePoll(getPollBackoff());
    return;
  }
  schedulePoll();
};

export const deactivationRunner = {
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  getSnapshot: (): DeactivationRun | null => run,

  setClient: (apolloClient: ApolloClient<object>) => {
    client = apolloClient;
  },

  setOnComplete: (fn: () => void) => {
    onComplete = fn;
  },

  setOnError: (fn: (message: string) => void) => {
    onError = fn;
  },

  start: async (ids: string[]) => {
    if (!client || ids.length === 0) return;
    try {
      const {data} = await client.mutate<
        StartBulkDeactivationMutation,
        StartBulkDeactivationMutationVariables
      >({
        mutation: StartBulkDeactivationDocument,
        variables: {ids},
      });
      const job = data?.startBulkDeactivation?.job;
      if (job) {
        applyJob(job);
        if (run && !isTerminal(run.status)) schedulePoll();
        else onComplete?.();
      }
    } catch {
      onError?.('Could not start deactivation. Please try again.');
    }
  },

  resumeFromStorage: () => {
    if (run) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const restoredRun = normalizeStoredRun(JSON.parse(raw));
      if (!restoredRun) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      run = restoredRun;
      notify();
      if (!isTerminal(run.status)) schedulePoll();
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  },

  dismiss: () => {
    stopPolling();
    run = null;
    persist();
    notify();
  },
};

export const getCounts = (
  current: DeactivationRun | null,
): DeactivationCounts => {
  if (!current) {
    return {total: 0, done: 0, succeeded: 0, failed: 0, isComplete: true};
  }
  return {
    total: current.total,
    done: current.processed,
    succeeded: current.succeeded,
    failed: current.failed,
    isComplete: isTerminal(current.status),
  };
};
